import { create, StateCreator } from "zustand";
import type { AgentRecord } from "../types";

interface Table {
  table_name: string;
  table_type: string;
}

interface Schema {
  schema_name: string;
}

interface Column {
  column_name: string;
  data_type: string;
  is_nullable: string;
}

interface TableData {
  columns: Column[];
  data: Record<string, any>[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalRows: number;
    limit: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  filters: {
    search: string;
    sortColumn: string;
    sortDirection: string;
    filterColumn: string;
    filterValue: string;
    filterOperator: string;
    filters?: { column: string; operator: string; value: string }[];
  };
}

interface FilterState {
  search: string;
  sortColumn: string;
  sortDirection: "asc" | "desc";
  filterColumn: string;
  filterValue: string;
  filterOperator: string;
  filters: { column: string; operator: string; value: string }[];
}

interface PendingFilters {
  search: string;
  filterColumn: string;
  filterValue: string;
  filterOperator: string;
  includeNull: boolean;
  filters: { column: string; operator: string; value: string }[];
}

export interface DbExplorerStateAndActions {
  schemas: Schema[];
  selectedSchema: string;
  tables: Table[];
  selectedTable: string;
  tableData: TableData | null;
  currentPage: number;
  pageSize: number;
  loading: boolean;
  tablesLoading: boolean;
  schemasLoading: boolean;
  error: string;
  appliedFilters: FilterState;
  pendingFilters: PendingFilters;

  isConnectionStringSet: boolean;
  connectionString: string | null;
  availableAgents: AgentRecord[];
  selectedAgentId: string;
  integrationsLoading: boolean;

  frozenColumnsCount: number;
  setFrozenColumnsCount: (count: number) => void;

  isFiltersPanelOpen: boolean;
  setIsFiltersPanelOpen: (open: boolean) => void;
  openSections: Record<string, boolean>;
  toggleSection: (sectionKey: string) => void;

  initializeConnectionString: () => Promise<void>;
  setSelectedAgentConnection: (agentId: string) => Promise<void>;
  fetchSchemas: () => Promise<void>;
  fetchTables: () => Promise<void>;
  fetchTableData: () => Promise<void>;
  setSelectedSchema: (schemaName: string) => void;
  setSelectedTable: (tableName: string) => void;
  setCurrentPage: (page: number) => void;
  setPageSize: (size: number) => void;
  setAppliedFilters: (filters: Partial<FilterState>) => void;
  setPendingFilters: (filters: Partial<PendingFilters>) => void;
  applyFilters: () => void;
  clearFilters: () => void;
  handleSort: (column: string) => void;
  exportCSV: (rowLimit?: number) => Promise<void>;
  getDataTypeCategory: (dataType: string) => string;
  getFilterOperators: (dataType: string) => { value: string; label: string }[];
}

const DB_SELECTED_SCHEMA_KEY = "dbExplorer.selectedSchema";
const DB_SELECTED_TABLE_KEY = "dbExplorer.selectedTable";
const DB_SELECTED_AGENT_KEY = "dbExplorer.selectedAgentId";

const getHeaders = (agentId: string) => {
  const headers = new Headers();
  headers.append("Content-Type", "application/json");
  headers.append("X-Datascope-Agent-Id", agentId);
  return headers;
};

const createDefaultAppliedFilters = (): FilterState => ({
  search: "",
  sortColumn: "created_at",
  sortDirection: "desc",
  filterColumn: "",
  filterValue: "",
  filterOperator: "contains",
  filters: [],
});

const createDefaultPendingFilters = (): PendingFilters => ({
  search: "",
  filterColumn: "",
  filterValue: "",
  filterOperator: "contains",
  includeNull: false,
  filters: [],
});

const createExplorerResetState = () => ({
  schemas: [] as Schema[],
  selectedSchema: "public",
  tables: [] as Table[],
  selectedTable: "",
  tableData: null as TableData | null,
  currentPage: 1,
  error: "",
  tablesLoading: false,
  schemasLoading: false,
  appliedFilters: createDefaultAppliedFilters(),
  pendingFilters: createDefaultPendingFilters(),
});

const getStringField = (value: unknown, candidates: string[]): string => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return "";
  }

  const record = value as Record<string, unknown>;
  for (const candidate of candidates) {
    const exact = record[candidate];
    if (typeof exact === "string" && exact.trim()) {
      return exact;
    }

    const matchedKey = Object.keys(record).find(
      (key) => key.toLowerCase() === candidate.toLowerCase(),
    );
    if (matchedKey) {
      const matchedValue = record[matchedKey];
      if (typeof matchedValue === "string" && matchedValue.trim()) {
        return matchedValue;
      }
    }
  }

  return "";
};

const normalizeSchema = (value: unknown): Schema => ({
  schema_name: getStringField(value, ["schema_name", "schema"]),
});

const normalizeTable = (value: unknown): Table => ({
  table_name: getStringField(value, ["table_name", "name"]),
  table_type: getStringField(value, ["table_type", "type"]),
});

const normalizeColumn = (value: unknown): Column => ({
  column_name: getStringField(value, ["column_name", "name"]),
  data_type: getStringField(value, ["data_type", "type"]),
  is_nullable: getStringField(value, ["is_nullable", "nullable"]),
});

const getIntegratedAgents = (agents: AgentRecord[]): AgentRecord[] =>
  agents.filter((agent) => Boolean(agent.postgresConfig.connectionString?.trim()));

const storeCreator: StateCreator<DbExplorerStateAndActions> = (set, get) => ({
  schemas: [],
  selectedSchema: "public",
  tables: [],
  selectedTable: "",
  tableData: null,
  currentPage: 1,
  pageSize: 50,
  loading: false,
  tablesLoading: false,
  schemasLoading: false,
  error: "",
  isConnectionStringSet: false,
  connectionString: null,
  availableAgents: [],
  selectedAgentId: "",
  integrationsLoading: false,
  frozenColumnsCount: 0,
  isFiltersPanelOpen: false,
  openSections: {
    search: true,
    sort: true,
    filters: true,
  },
  appliedFilters: createDefaultAppliedFilters(),
  pendingFilters: createDefaultPendingFilters(),

  initializeConnectionString: async () => {
    set({ integrationsLoading: true });

    let nextAgents: AgentRecord[] = [];
    try {
      const response = await fetch("/auth/agents", { credentials: "include" });
      if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`);
      }
      nextAgents = (await response.json()) as AgentRecord[];
    } catch (_error) {
      set({
        availableAgents: [],
        integrationsLoading: false,
      });
    }

    const availableAgents = getIntegratedAgents(nextAgents);
    const storedAgentId = localStorage.getItem(DB_SELECTED_AGENT_KEY);
    set({
      availableAgents,
      integrationsLoading: false,
    });

    if (availableAgents.length > 0) {
      const preferredAgent =
        availableAgents.find((agent) => agent.id === storedAgentId) ?? availableAgents[0];

      if (preferredAgent) {
        await get().setSelectedAgentConnection(preferredAgent.id);
        return;
      }
    }

    set({
      ...createExplorerResetState(),
      connectionString: null,
      isConnectionStringSet: false,
      availableAgents,
      selectedAgentId: "",
      integrationsLoading: false,
    });
  },

  setSelectedAgentConnection: async (agentId: string) => {
    const agent = get().availableAgents.find((entry) => entry.id === agentId);
    if (!agent?.postgresConfig.connectionString) {
      set({ error: "Selected integration does not have a database connection." });
      return;
    }

    localStorage.setItem(DB_SELECTED_AGENT_KEY, agent.id);
    set({
      ...createExplorerResetState(),
      connectionString: agent.postgresConfig.connectionString,
      isConnectionStringSet: true,
      selectedAgentId: agent.id,
      schemasLoading: true,
    });
    await get().fetchSchemas();
  },

  fetchSchemas: async () => {
    if (!get().isConnectionStringSet) {
      set({ error: "Database connection string not configured.", schemasLoading: false });
      return;
    }
    set({ schemasLoading: true, error: "" });
    try {
      const response = await fetch(`/api/datascope/schemas`, {
        headers: getHeaders(get().selectedAgentId),
      });
      const result = await response.json();
      if (response.ok) {
        const normalizedSchemas: Schema[] = Array.isArray(result.schemas)
          ? result.schemas
              .map((value: unknown) => normalizeSchema(value))
              .filter((schema: Schema) => schema.schema_name)
          : [];
        set({ schemas: normalizedSchemas, schemasLoading: false });
        const storedSchema = localStorage.getItem(DB_SELECTED_SCHEMA_KEY);
        const currentSchema = get().selectedSchema;
        const schemaNames: string[] = normalizedSchemas.map((schema) => schema.schema_name);
        let schemaToUse = currentSchema;

        if (storedSchema && schemaNames.includes(storedSchema)) {
          schemaToUse = storedSchema;
        } else if (!schemaNames.includes(currentSchema)) {
          const defaultSchema =
            normalizedSchemas.find((schema) => schema.schema_name === "public") ||
            normalizedSchemas[0];
          schemaToUse = defaultSchema?.schema_name || "";
        }

        if (schemaToUse && schemaToUse !== currentSchema) {
          get().setSelectedSchema(schemaToUse);
        } else {
          get().fetchTables();
        }
      } else {
        set({ error: result.error || "Failed to fetch schemas", schemasLoading: false });
      }
    } catch (_err) {
      set({
        error: "Network error or failed to connect to the database API.",
        schemasLoading: false,
      });
    }
  },

  fetchTables: async () => {
    if (!get().isConnectionStringSet) {
      set({ error: "Database connection string not configured.", tablesLoading: false });
      return;
    }
    set({ tablesLoading: true, error: "" });
    try {
      const { selectedSchema } = get();
      const params = new URLSearchParams({ schema: selectedSchema });
      const response = await fetch(`/api/datascope/tables?${params}`, {
        headers: getHeaders(get().selectedAgentId),
      });
      const result = await response.json();
      if (response.ok) {
        const normalizedTables: Table[] = Array.isArray(result.tables)
          ? result.tables
              .map((value: unknown) => normalizeTable(value))
              .filter((table: Table) => table.table_name)
          : [];
        set({ tables: normalizedTables, tablesLoading: false });
        const tableNames: string[] = normalizedTables.map((table) => table.table_name);
        if (normalizedTables.length > 0) {
          const storedSchema = localStorage.getItem(DB_SELECTED_SCHEMA_KEY);
          const storedTable = localStorage.getItem(DB_SELECTED_TABLE_KEY);
          const currentSchema = get().selectedSchema;
          const currentSelectedTable = get().selectedTable;

          if (
            storedSchema === currentSchema &&
            storedTable &&
            tableNames.includes(storedTable)
          ) {
            if (currentSelectedTable !== storedTable) {
              get().setSelectedTable(storedTable);
            }
          } else if (!currentSelectedTable) {
            get().setSelectedTable(normalizedTables[0]!.table_name);
          }
        } else {
          set({ selectedTable: "", tableData: null });
        }
      } else {
        set({ error: result.error || "Failed to fetch tables", tablesLoading: false });
      }
    } catch (_err) {
      set({ error: "Network error or failed to connect to the database API.", tablesLoading: false });
    }
  },

  fetchTableData: async () => {
    const {
      selectedTable,
      selectedSchema,
      currentPage,
      pageSize,
      appliedFilters,
      isConnectionStringSet,
    } = get();
    if (!selectedTable) return;
    if (!isConnectionStringSet) {
      set({ error: "Database connection string not configured.", loading: false });
      return;
    }

    set({ loading: true, error: "" });
    try {
      const params = new URLSearchParams({
        schema: selectedSchema,
        page: currentPage.toString(),
        limit: pageSize.toString(),
        search: appliedFilters.search,
        sortColumn: appliedFilters.sortColumn,
        sortDirection: appliedFilters.sortDirection,
        filterColumn: appliedFilters.filterColumn,
        filterValue: appliedFilters.filterValue,
        filterOperator: appliedFilters.filterOperator,
      });
      try {
        if (appliedFilters.filters && appliedFilters.filters.length > 0) {
          params.append("filters", JSON.stringify(appliedFilters.filters));
        }
      } catch (_) {}
      const response = await fetch(`/api/datascope/tables/${selectedTable}?${params}`, {
        headers: getHeaders(get().selectedAgentId),
      });
      const result = await response.json();
      if (response.ok) {
        set({
          tableData: {
            ...result,
            columns: Array.isArray(result.columns)
              ? result.columns
                  .map((value: unknown) => normalizeColumn(value))
                  .filter((column: Column) => column.column_name)
              : [],
          },
        });
      } else {
        set({ error: result.error || "Failed to fetch table data" });
      }
    } catch (_err) {
      set({ error: "Failed to fetch table data" });
    } finally {
      set({ loading: false });
    }
  },

  setSelectedSchema: (schemaName: string) => {
      set({
        selectedSchema: schemaName,
        selectedTable: "",
        currentPage: 1,
        tableData: null,
        tables: [],
        appliedFilters: createDefaultAppliedFilters(),
        pendingFilters: createDefaultPendingFilters(),
      });
    try {
      localStorage.setItem(DB_SELECTED_SCHEMA_KEY, schemaName);
    } catch (_) {}
    get().fetchTables();
  },

  setSelectedTable: (tableName: string) => {
    set({
      selectedTable: tableName,
      currentPage: 1,
      tableData: null,
      appliedFilters: createDefaultAppliedFilters(),
      pendingFilters: createDefaultPendingFilters(),
    });
    try {
      const currentSchema = get().selectedSchema;
      if (currentSchema) localStorage.setItem(DB_SELECTED_SCHEMA_KEY, currentSchema);
      localStorage.setItem(DB_SELECTED_TABLE_KEY, tableName);
    } catch (_) {}
    get().fetchTableData();
  },

  setCurrentPage: (page: number) => {
    set({ currentPage: page });
    get().fetchTableData();
  },

  setPageSize: (size: number) => {
    set({ pageSize: size, currentPage: 1 });
    get().fetchTableData();
  },

  setAppliedFilters: (filters: Partial<FilterState>) => {
    set((state) => ({ appliedFilters: { ...state.appliedFilters, ...filters } }));
  },

  setPendingFilters: (filters: Partial<PendingFilters>) => {
    set((state) => ({ pendingFilters: { ...state.pendingFilters, ...filters } }));
  },

  applyFilters: () => {
    const { pendingFilters, appliedFilters } = get();
    set({
      appliedFilters: {
        ...appliedFilters,
        search: pendingFilters.search,
        filterColumn: pendingFilters.filters[0]?.column || pendingFilters.filterColumn,
        filterValue: pendingFilters.filters[0]?.value || pendingFilters.filterValue,
        filterOperator: pendingFilters.filters[0]?.operator || pendingFilters.filterOperator,
        filters: pendingFilters.filters,
      },
      currentPage: 1,
    });
    get().fetchTableData();
  },

  clearFilters: () => {
    set({
      appliedFilters: createDefaultAppliedFilters(),
      pendingFilters: createDefaultPendingFilters(),
      currentPage: 1,
    });
    get().fetchTableData();
  },

  handleSort: (column: string) => {
    const { appliedFilters } = get();
    const newSortDirection =
      appliedFilters.sortColumn === column && appliedFilters.sortDirection === "asc"
        ? "desc"
        : "asc";
    set({
      appliedFilters: { ...appliedFilters, sortColumn: column, sortDirection: newSortDirection },
      currentPage: 1,
    });
    get().fetchTableData();
  },

  setFrozenColumnsCount: (count: number) => {
    set({ frozenColumnsCount: Math.max(0, count) });
  },

  setIsFiltersPanelOpen: (open: boolean) => {
    set({ isFiltersPanelOpen: open });
  },

  toggleSection: (sectionKey: string) => {
    set((state) => ({
      openSections: {
        ...state.openSections,
        [sectionKey]: !state.openSections[sectionKey],
      },
    }));
  },

  exportCSV: async (rowLimit?: number) => {
    const { selectedTable, selectedSchema, appliedFilters, isConnectionStringSet } = get();
    if (!selectedTable) return;
    if (!isConnectionStringSet) {
      set({ error: "Database connection string not configured." });
      return;
    }

    set({ error: "" });
    try {
      const params = new URLSearchParams({
        schema: selectedSchema,
        search: appliedFilters.search,
        sortColumn: appliedFilters.sortColumn,
        sortDirection: appliedFilters.sortDirection,
        filterColumn: appliedFilters.filterColumn,
        filterValue: appliedFilters.filterValue,
        filterOperator: appliedFilters.filterOperator,
      });

      if (rowLimit && rowLimit > 0) {
        params.append("limit", rowLimit.toString());
      }

      try {
        if (appliedFilters.filters && appliedFilters.filters.length > 0) {
          params.append("filters", JSON.stringify(appliedFilters.filters));
        }
      } catch (_) {}

      const response = await fetch(
        `/api/datascope/tables/${selectedTable}/export?${params}`,
        { headers: getHeaders(get().selectedAgentId) },
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;
        a.download =
          response.headers
            .get("content-disposition")
            ?.split("filename=")[1]
            ?.replace(/"/g, "") || `${selectedTable}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        set({ error: "Failed to export data" });
      }
    } catch (_err) {
      set({ error: "Failed to export data" });
    }
  },

  getDataTypeCategory: (dataType: string) => {
    const type = dataType.toLowerCase();
    if (type.includes("bool")) return "boolean";
    if (
      type.includes("int") ||
      type.includes("numeric") ||
      type.includes("decimal") ||
      type.includes("float") ||
      type.includes("double") ||
      type.includes("real") ||
      type.includes("money")
    )
      return "number";
    if (
      type.includes("date") ||
      type.includes("time") ||
      type.includes("timestamp") ||
      type.includes("interval")
    )
      return "datetime";
    if (
      type.includes("varchar") ||
      type.includes("text") ||
      type.includes("char") ||
      type.includes("string")
    )
      return "text";
    return "other";
  },

  getFilterOperators: (dataType: string) => {
    const category = get().getDataTypeCategory(dataType);
    switch (category) {
      case "boolean":
        return [
          { value: "equals", label: "Is" },
          { value: "not_null", label: "Not null" },
          { value: "is_null", label: "Is null" },
        ];
      case "number":
        return [
          { value: "equals", label: "Equals" },
          { value: "greater_than", label: "Greater than" },
          { value: "less_than", label: "Less than" },
          { value: "not_null", label: "Not null" },
          { value: "is_null", label: "Is null" },
        ];
      case "datetime":
        return [
          { value: "equals", label: "On date" },
          { value: "greater_than", label: "After date" },
          { value: "less_than", label: "Before date" },
          { value: "greater_than_or_equal", label: "On or after" },
          { value: "less_than_or_equal", label: "On or before" },
          { value: "not_null", label: "Not null" },
          { value: "is_null", label: "Is null" },
        ];
      default:
        return [
          { value: "contains", label: "Contains" },
          { value: "equals", label: "Equals" },
          { value: "starts_with", label: "Starts with" },
          { value: "ends_with", label: "Ends with" },
          { value: "not_null", label: "Not null" },
          { value: "is_null", label: "Is null" },
        ];
    }
  },
});

export const useDbExplorerStore = create<DbExplorerStateAndActions>(storeCreator);
export const useDbExplorer = useDbExplorerStore;
